// Selector helpers for finding trackable buttons and fields from DOM event paths.
import { PassiveTrackingDom } from 'app/shared/services/fullstory/quote-passive-tracking/quote-passive-tracking.dom';
import { matches } from 'app/shared/utils/dom/dom-helpers';

export class PassiveTrackingSelectors {
  /**
   * Walks the event path and returns the first element that acts like a button we care about:
   * a real <button>, a radio card, a <summary>, a role="button", a trackable anchor, or a submit/button input.
   */
  static findTrackableButton(path: HTMLElement[], origin: string): HTMLElement | null {
    return (
      path.find((element) => {
        if (matches(element, 'button')) return true;
        if (matches(element, PassiveTrackingDom.BUTTON_INPUT_SELECTOR)) return true;
        if (matches(element, PassiveTrackingDom.RADIO_CARD_SELECTOR)) return true;
        if (matches(element, 'summary')) return true;
        if (matches(element, '[role="button"]')) return true;
        if (matches(element, 'a')) {
          return PassiveTrackingDom.isTrackableAnchor(element, origin);
        }
        return false;
      }) ?? null
    );
  }

  /** Returns the first form field (input, select, textarea, or calendar-date) in the event path. */
  static findTrackableField(path: HTMLElement[]): HTMLElement | null {
    return path.find((element) => matches(element, PassiveTrackingDom.FIELD_SELECTOR)) ?? null;
  }

  /** Skips buttons that are disabled, hidden, or living inside a custom widget we don't track piece-by-piece. */
  static isIgnoredButton(element: HTMLElement, path: HTMLElement[]): boolean {
    if ((element as HTMLButtonElement).disabled) return true;
    if (element.getAttribute('aria-disabled') === 'true') return true;
    if (element.offsetParent === null) return true;
    if (PassiveTrackingDom.pathIncludesIgnoredWidget(path)) return true;

    return false;
  }

  /** Skips fields we don't care about: disabled, hidden, readonly, radios, and submit/reset/button/hidden inputs. */
  static isIgnoredField(element: HTMLElement): boolean {
    if ((element as HTMLInputElement).disabled) return true;
    if (element.getAttribute('aria-disabled') === 'true') return true;
    if (element.getAttribute('readonly') !== null) return true;
    // calendar-date can render as interactable while reporting no offsetParent.
    if (element.offsetParent === null && element.tagName.toLowerCase() !== 'calendar-date') return true;

    if (element instanceof HTMLInputElement) {
      if (element.type === 'radio') return true;
      return ['hidden', 'submit', 'button', 'reset'].includes(element.type);
    }

    return false;
  }

  /**
   * Skips interactions that came from inside a custom widget (calendar, address autocomplete)
   * rather than from the field itself — we already track the widget at a higher level.
   */
  static isIgnoredFieldInteraction(field: HTMLElement, path: HTMLElement[]): boolean {
    const firstElement = path[0];
    if (!firstElement) return false;

    if (field === firstElement) return false;

    return PassiveTrackingDom.pathIncludesIgnoredWidget(path);
  }
}
